import { Button } from "@/components/ui/button"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { Skeleton } from "@/components/ui/skeleton"
import { MessageCircle, Trash2, Heart, Image, Smile, X } from "lucide-react"
import { useState, useRef } from "react"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/components/ui/use-toast"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { UserProfileHoverCard } from "./UserProfileHoverCard"
import { Comment } from "@/types/comment.types"
import { MentionInput } from "./MentionInput"
import { GifPicker } from "./post/GifPicker"
import { cn } from "@/lib/utils"
import { useQueryClient } from "@tanstack/react-query"
import { useIsAdmin } from "@/hooks/useUserRole"

interface PostCommentsProps {
  comments: Comment[]
  newComment: string
  isLoading: boolean
  isSubmitting?: boolean
  onCommentChange: (value: string) => void
  onSubmitComment: (image_url?: string, gif_url?: string) => void
  replyingTo: string | null
  setReplyingTo: (id: string | null) => void
  replyContent: string
  setReplyContent: (value: string) => void
  onSubmitReply: (parentId: string, image_url?: string, gif_url?: string) => void
  isAddingReply: boolean
  postId: string
}

export function PostComments({
  comments,
  newComment,
  isLoading,
  isSubmitting = false,
  onCommentChange,
  onSubmitComment,
  replyingTo,
  setReplyingTo,
  replyContent,
  setReplyContent,
  onSubmitReply,
  isAddingReply,
  postId
}: PostCommentsProps) {
  const { isAdmin } = useIsAdmin()
  const [isDeletingComment, setIsDeletingComment] = useState<string | null>(null)
  const [likingComment, setLikingComment] = useState<string | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Media state for main comment
  const [commentImage, setCommentImage] = useState<File | null>(null)
  const [commentImagePreview, setCommentImagePreview] = useState<string | null>(null)
  const [commentGif, setCommentGif] = useState<string | null>(null)
  const [showGifPicker, setShowGifPicker] = useState(false)
  const commentFileRef = useRef<HTMLInputElement>(null)

  // Media state for reply
  const [replyImage, setReplyImage] = useState<File | null>(null)
  const [replyImagePreview, setReplyImagePreview] = useState<string | null>(null)
  const [replyGif, setReplyGif] = useState<string | null>(null)
  const [showReplyGifPicker, setShowReplyGifPicker] = useState(false)
  const replyFileRef = useRef<HTMLInputElement>(null)

  const [isUploading, setIsUploading] = useState(false)

  const uploadImage = async (file: File): Promise<string | null> => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null
    const ext = file.name.split('.').pop()
    const path = `comments/${user.id}/${Date.now()}.${ext}`
    const { error } = await supabase.storage.from('community-images').upload(path, file)
    if (error) {
      console.error('Upload error:', error)
      return null
    }
    const { data: publicUrl } = supabase.storage.from('community-images').getPublicUrl(path)
    return publicUrl.publicUrl
  }

  const handleImageSelect = (file: File, target: 'comment' | 'reply') => {
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Arquivo muito grande", description: `${file.name} excede 2MB`, variant: "destructive" })
      return
    }
    const url = URL.createObjectURL(file)
    if (target === 'comment') {
      setCommentImage(file)
      setCommentImagePreview(url)
      setCommentGif(null)
    } else {
      setReplyImage(file)
      setReplyImagePreview(url)
      setReplyGif(null)
    }
  }

  const handleGifSelect = (gifUrl: string, target: 'comment' | 'reply') => {
    if (target === 'comment') {
      setCommentGif(gifUrl)
      setCommentImage(null)
      setCommentImagePreview(null)
    } else {
      setReplyGif(gifUrl)
      setReplyImage(null)
      setReplyImagePreview(null)
    }
  }

  const clearMedia = (target: 'comment' | 'reply') => {
    if (target === 'comment') {
      setCommentImage(null)
      setCommentImagePreview(null)
      setCommentGif(null)
    } else {
      setReplyImage(null)
      setReplyImagePreview(null)
      setReplyGif(null)
    }
  }

  const handleSubmitComment = async () => {
    if (!newComment.trim() && !commentImage && !commentGif) return
    setIsUploading(true)
    try {
      let imageUrl: string | undefined
      if (commentImage) {
        const url = await uploadImage(commentImage)
        if (url) imageUrl = url
      }
      onSubmitComment(imageUrl, commentGif || undefined)
      clearMedia('comment')
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmitReply = async (parentId: string) => {
    if (!replyContent.trim() && !replyImage && !replyGif) return
    setIsUploading(true)
    try {
      let imageUrl: string | undefined
      if (replyImage) {
        const url = await uploadImage(replyImage)
        if (url) imageUrl = url
      }
      onSubmitReply(parentId, imageUrl, replyGif || undefined)
      clearMedia('reply')
    } finally {
      setIsUploading(false)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    try {
      setIsDeletingComment(commentId)
      const { error } = await supabase
        .from('community_post_comments')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', commentId)

      if (error) throw error

      queryClient.invalidateQueries({ queryKey: ['comments', postId] })
      queryClient.invalidateQueries({ queryKey: ['posts'] })
      queryClient.invalidateQueries({ queryKey: ['introductions-posts'] })
      queryClient.invalidateQueries({ queryKey: ['learning-posts'] })
      queryClient.invalidateQueries({ queryKey: ['questions-posts'] })
      

      toast({ title: "Comentário removido", description: "O comentário foi removido com sucesso." })
    } catch (error) {
      console.error('Error deleting comment:', error)
      toast({ title: "Erro ao remover comentário", description: "Ocorreu um erro ao tentar remover o comentário.", variant: "destructive" })
    } finally {
      setIsDeletingComment(null)
    }
  }

  const handleToggleLike = async (commentId: string, isLiked: boolean) => {
    try {
      setLikingComment(commentId)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        toast({ title: "Faça login", description: "Você precisa estar logado para curtir comentários.", variant: "destructive" })
        return
      }
      if (isLiked) {
        await supabase.from('community_comment_likes').delete().eq('comment_id', commentId).eq('user_id', user.id)
      } else {
        await supabase.from('community_comment_likes').insert({ comment_id: commentId, user_id: user.id })
      }
      queryClient.invalidateQueries({ queryKey: ['comments', postId] })
    } catch (error) {
      console.error('Error toggling comment like:', error)
      toast({ title: "Erro", description: "Não foi possível processar sua ação.", variant: "destructive" })
    } finally {
      setLikingComment(null)
    }
  }

  const formatDate = (date: string) => {
    const commentDate = new Date(date)
    const now = new Date()
    const diffInHours = Math.abs(now.getTime() - commentDate.getTime()) / 36e5
    if (diffInHours < 24) return format(commentDate, "'Hoje às' HH:mm", { locale: ptBR })
    if (diffInHours < 48) return format(commentDate, "'Ontem às' HH:mm", { locale: ptBR })
    return format(commentDate, "d 'de' MMMM 'às' HH:mm", { locale: ptBR })
  }

  const formatContent = (content: string) => content.replace(/@\[([^\]]+)\]/g, (_, name) => `@${name}`)

  const MediaToolbar = ({ target }: { target: 'comment' | 'reply' }) => (
    <div className="flex items-center gap-1">
      <input
        type="file"
        accept="image/*"
        className="hidden"
        ref={target === 'comment' ? commentFileRef : replyFileRef}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleImageSelect(file, target)
          e.target.value = ''
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={() => (target === 'comment' ? commentFileRef : replyFileRef).current?.click()}
      >
        <Image className="h-4 w-4 text-muted-foreground" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={() => target === 'comment' ? setShowGifPicker(true) : setShowReplyGifPicker(true)}
      >
        <Smile className="h-4 w-4 text-muted-foreground" />
      </Button>
    </div>
  )

  const MediaPreview = ({ target }: { target: 'comment' | 'reply' }) => {
    const preview = target === 'comment' ? commentImagePreview : replyImagePreview
    const gif = target === 'comment' ? commentGif : replyGif
    if (!preview && !gif) return null
    return (
      <div className="relative inline-block mt-2">
        <img src={preview || gif || ''} alt="Preview" className="max-h-32 rounded-lg border" />
        <Button
          type="button"
          variant="destructive"
          size="sm"
          className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
          onClick={() => clearMedia(target)}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    )
  }

  const renderComment = (comment: Comment, depth: number = 0) => {
    const maxDepth = 2
    const canReply = depth < maxDepth

    return (
      <div key={comment.id} className={depth > 0 ? "ml-8 mt-3 border-l-2 border-border pl-4" : ""}>
        <div className="group rounded-lg border p-4 transition-colors hover:bg-muted/50">
          <div className="flex justify-between items-start mb-2">
            <div className="flex gap-2 flex-1">
              <Avatar className="w-10 h-10 border border-border">
                <AvatarImage src={comment.author?.logo_url} />
                <AvatarFallback>{(comment.author?.full_name || 'U').charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <UserProfileHoverCard userId={comment.author?.id ?? ''}>
                    <span className="font-medium text-foreground hover:text-primary cursor-pointer transition-colors">
                      {(comment.author?.full_name || 'Usuário removido')}
                    </span>
                  </UserProfileHoverCard>
                  <span className="text-sm text-muted-foreground">{formatDate(comment.created_at)}</span>
                </div>
                <p className="text-foreground/80 whitespace-pre-wrap mt-2">{formatContent(comment.content)}</p>

                {/* Comment media */}
                {comment.image_url && (
                  <img src={comment.image_url} alt="Imagem" className="mt-2 rounded-lg max-h-64 w-auto border" loading="lazy" />
                )}
                {comment.gif_url && (
                  <img src={comment.gif_url} alt="GIF" className="mt-2 rounded-lg max-h-48 w-auto" loading="lazy" />
                )}

                <div className="flex items-center gap-2 mt-2">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleToggleLike(comment.id, comment.is_liked || false)} disabled={likingComment === comment.id}>
                    <Heart className={cn("h-3 w-3 mr-1 transition-colors", comment.is_liked && "fill-red-500 text-red-500")} />
                    {comment.likes_count || 0}
                  </Button>
                  {canReply && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}>
                      <MessageCircle className="h-3 w-3 mr-1" />
                      Responder
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => handleDeleteComment(comment.id)} disabled={isDeletingComment === comment.id}>
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remover
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {replyingTo === comment.id && (
            <div className="mt-3 space-y-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <MentionInput
                    placeholder={`Responder para ${(comment.author?.full_name || 'Usuário removido')}...`}
                    value={replyContent}
                    onChange={setReplyContent}
                    disabled={isAddingReply || isUploading}
                    minHeight="60px"
                    className="text-sm"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <MediaToolbar target="reply" />
                  </div>
                  <MediaPreview target="reply" />
                </div>
                <div className="flex flex-col gap-2">
                  <Button size="sm" onClick={() => handleSubmitReply(comment.id)} disabled={(!replyContent.trim() && !replyImage && !replyGif) || isAddingReply || isUploading}>
                    {isAddingReply || isUploading ? "Enviando..." : "Enviar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyContent(''); clearMedia('reply') }} disabled={isAddingReply}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-2 space-y-2">
            {comment.replies.map((reply) => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="w-full space-y-4">
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <MentionInput
              placeholder="Escreva um comentário... Use @ para mencionar alguém"
              value={newComment}
              onChange={onCommentChange}
              disabled={isSubmitting || isUploading}
              minHeight="60px"
            />
            <div className="flex items-center justify-between mt-1">
              <MediaToolbar target="comment" />
            </div>
            <MediaPreview target="comment" />
          </div>
          <Button
            onClick={handleSubmitComment}
            disabled={(!newComment.trim() && !commentImage && !commentGif) || isSubmitting || isUploading}
            className="w-full sm:w-auto shrink-0"
          >
            {isSubmitting || isUploading ? "Enviando..." : "Comentar"}
          </Button>
        </div>
      </div>

      <GifPicker open={showGifPicker} onOpenChange={setShowGifPicker} onSelect={(url) => handleGifSelect(url, 'comment')} />
      <GifPicker open={showReplyGifPicker} onOpenChange={setShowReplyGifPicker} onSelect={(url) => handleGifSelect(url, 'reply')} />

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="w-full rounded-md border p-3 sm:p-4">
          {comments.length === 0 ? (
            <p className="text-center text-muted-foreground">Nenhum comentário ainda. Seja o primeiro a comentar!</p>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => renderComment(comment))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
